import create from 'zustand';
import produce from 'immer';
import { orderBy, last, uniqBy } from 'lodash';

// Order conversations by last message
const orderConversations = (conversations) => {
	return orderBy(conversations, (conversation => {
		const latest = last(conversation.messages);
		return latest?.createdAt;
	}), 'desc');
};

const MESSENGER_INITIAL = {
	// Can be conversations, contacts, chat
	view: 'conversations',

	// Store collected conversations
	conversations: [],

	// Store collected contacts
	contacts: [],

	// Store active conversation
	active: 0,

	// Store pending conversation
	pending: {
		id: 0,
		role: '',
		online: false,
		avatar: '',
		fullName: ''
	}
};

export const useMessengerStore = create(set => ({
	...MESSENGER_INITIAL,

	// Update messenger view
	updateView: (view) => set(produce(state => {
		state.view = view;

		if (view !== 'chat') {
			state.pending = {
				id: 0,
				role: '',
				online: false,
				avatar: '',
				fullName: ''
			};
			state.active = 0;
		}
	})),

	// Update pending conversation
	onContact: (contact) => set(produce(state => {
		// Update view to chat
		state.view = 'chat';

		// Find existing conversation with contact
		const conversation = state.conversations.find(c => c.type === 'private' && c.participants.some(p => p.id === contact.id));

		// Update pending contact information
		if (conversation?.id) {
			state.active = conversation.id;
		} else {
			state.pending = {
				id: contact.id,
				role: contact.role,
				online: contact.online,
				avatar: contact.avatar,
				fullName: contact.fullName
			};
		}
	})),

	// Update active conversation
	onActive: (id) => set(produce(state => {
		state.view = 'chat';

		// Update pending contact information
		state.pending = {
			id: 0,
			role: '',
			online: false,
			avatar: '',
			fullName: ''
		};

		// Update active conversation
		state.active = id;
	})),

	// Initialize conversations
	onInit: (conversations) => set(produce(state => {
		state.conversations = orderConversations(conversations);
	})),

	// Add new conversation
	onAddConversation: (conversation) => set(produce(state => {
		state.conversations = orderConversations([conversation, ...state.conversations]);
	})),

	// Add new message
	onAddMessage: (message) => set(produce(state => {
		const conversation = state.conversations?.find(c => c.id === message.conversationId);
		conversation.messages.push(message);
		state.conversations = orderConversations(state.conversations);
	})),

	// Add new readby record
	onReadMessage: (data) => set(produce(state => {
		const conversation = state.conversations?.find(c => c.id === data.conversationId);
		const message = conversation.messages?.find(m => m.id === data.readby.messageId);
		message.readby.push(data.readby);
	})),

	// Add new contacts
	onAddContacts: (contacts, flush) => set(produce(state => {
		state.contacts = flush
			? contacts
		 	: uniqBy([...state.contacts, ...contacts], 'id');
	})),

	// Reset app's state
	onReset: () => set({
		...MESSENGER_INITIAL
	})
}));